import React from 'react';
import { Box, Grid, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import BodyBanner from '../components/home/body-banner';
import QuickAccessCard, { QuickAccessGrid } from '../components/home/quick-access-card';
import HomeSection, { HomeSectionVariant } from '../components/home/home-section';
import { clickableCardSx, READING_WIDTH, staticCardSx } from '../../theme/tokens';
import { RichTextContent } from '../custom-page/rich-text-content';
import SectionNews from './sections/section-news';
import SectionPublicationsTabs from './sections/section-publications-tabs';
import { useMenuLinkNavigation } from '../../hooks/useMenuLinkNavigation';
import { youtubeEmbedUrl } from '../../utils/youtube';
import { IBannerReponse } from '../../model/base/response/banner-response';
import {
  HomeSectionType,
  IPortalHomeSectionItemResponse,
  IPortalHomeSectionResponse,
} from '../../model/base/response/portal-home-response';

/** Quantidade padrão de notícias quando a seção não define (mesma da home padrão). */
export const DEFAULT_NEWS_QUANTITY = 4;

// Ícones do Acesso Rápido: chaves gravadas pelo APM (ACESSO_RAPIDO_ICONS) → ícones FontAwesome do portal
const ACCESS_ICONS: Record<string, IconName> = {
  description: 'file-lines',
  gavel: 'gavel',
  account_balance: 'landmark',
  payments: 'money-bill-wave',
  receipt_long: 'receipt',
  shopping_cart: 'cart-shopping',
  work: 'briefcase',
  groups: 'users',
  support_agent: 'headset',
  campaign: 'bullhorn',
  visibility: 'eye',
  event: 'calendar-days',
  newspaper: 'newspaper',
  menu_book: 'book-open',
  health_and_safety: 'shield-heart',
  school: 'graduation-cap',
  phone: 'phone',
  place: 'location-dot',
  link: 'link',
};

interface SectionProps {
  section: IPortalHomeSectionResponse;
  variant: HomeSectionVariant;
}

// Banner de corpo: posicionado entre as seções, com layout próprio (o carrossel do topo é renderizado pela HomeComponent)
function BannersSection({ section, variant }: SectionProps) {
  const sectionBanners: IBannerReponse[] = (section.items || [])
    .filter((item) => !!item.imageUrl)
    .map((item) => ({
      id: item.id,
      title: item.title,
      urlImage: item.imageUrl,
      nomImgBanner: item.image,
      url: item.url,
      openInNewTab: item.openInNewTab,
    }));

  if (sectionBanners.length === 0) return null;

  return (
    <HomeSection variant={variant} title={section.title} subtitle={section.subtitle}>
      <BodyBanner banners={sectionBanners} />
    </HomeSection>
  );
}

function FreeTextSection({ section, variant }: SectionProps) {
  if (!section.content) return null;

  return (
    <HomeSection variant={variant} title={section.title} subtitle={section.subtitle}>
      <Box sx={{ maxWidth: READING_WIDTH }}>
        <RichTextContent html={section.content} />
      </Box>
    </HomeSection>
  );
}

/** Link externo cadastrado sem protocolo vira https; o card só trata como externo o que começa com http. */
const accessHref = (url: string | undefined) => {
  if (!url) return '#';
  return /^[\w-]+(\.[\w-]+)+(\/|$)/i.test(url) ? `https://${url}` : url;
};

function QuickAccessSection({ section, variant }: SectionProps) {
  if (!section.items?.length) return null;

  return (
    <HomeSection variant={variant} title={section.title || 'Acesso rápido'} subtitle={section.subtitle}>
      <QuickAccessGrid>
        {(section.items || []).map((item) => (
          <QuickAccessCard
            key={item.id}
            title={item.title || ''}
            icon={(item.icon && ACCESS_ICONS[item.icon]) || 'link'}
            href={accessHref(item.url)}
            openInNewTab={item.openInNewTab}
          />
        ))}
      </QuickAccessGrid>
    </HomeSection>
  );
}

function HighlightCard({ item }: { item: IPortalHomeSectionItemResponse }) {
  const openLink = useMenuLinkNavigation();
  const clickable = !!item.url;

  return (
    <Box
      {...(clickable && {
        component: 'button' as const,
        type: 'button',
        onClick: () => openLink(item.url, item.openInNewTab),
      })}
      sx={{
        ...((clickable ? clickableCardSx : staticCardSx) as object),
        height: '100%',
        width: '100%',
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        textAlign: 'left',
        font: 'inherit',
        '&:hover .highlight-image': { transform: 'scale(1.03)' },
      }}
    >
      {item.imageUrl && (
        <Box sx={{ aspectRatio: '16 / 9', overflow: 'hidden', width: '100%' }}>
          <Box
            component="img"
            src={item.imageUrl}
            alt=""
            loading="lazy"
            className="highlight-image"
            sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)' }}
          />
        </Box>
      )}
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1, flexGrow: 1, width: '100%' }}>
        <Typography variant="h4" component="h3" sx={{ fontSize: '1.125rem' }}>
          {item.title}
        </Typography>
        {item.description && (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
            {item.description}
          </Typography>
        )}
        {clickable && (
          <Typography
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 'auto', pt: 1, color: 'brand.text', fontWeight: 600, fontSize: '0.9375rem' }}
          >
            Saiba mais <ArrowForwardIcon sx={{ fontSize: '1rem' }} />
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function HighlightCardsSection({ section, variant }: SectionProps) {
  const items = section.items || [];
  if (!items.length) return null;

  const md = items.length >= 3 ? 4 : 12 / items.length;

  return (
    <HomeSection variant={variant} title={section.title || 'Destaques'} subtitle={section.subtitle}>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {items.map((item) => (
          <Grid item xs={12} sm={6} md={md} key={item.id}>
            <HighlightCard item={item} />
          </Grid>
        ))}
      </Grid>
    </HomeSection>
  );
}

function VideosSection({ section, variant }: SectionProps) {
  const videos = (section.items || [])
    .map((item) => ({ item, embedUrl: youtubeEmbedUrl(item.url) }))
    .filter((video): video is { item: IPortalHomeSectionItemResponse; embedUrl: string } => !!video.embedUrl);

  if (!videos.length) return null;

  const md = videos.length >= 3 ? 4 : 12 / videos.length;

  return (
    <HomeSection variant={variant} title={section.title || 'Vídeos'} subtitle={section.subtitle}>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        {videos.map(({ item, embedUrl }) => (
          <Grid item xs={12} sm={6} md={md} key={item.id}>
            <Box sx={{ ...(staticCardSx as object), overflow: 'hidden', height: '100%' }}>
              <Box sx={{ position: 'relative', aspectRatio: '16 / 9', backgroundColor: 'grey.900' }}>
                <Box
                  component="iframe"
                  src={embedUrl}
                  title={item.title || 'Vídeo'}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                />
              </Box>
              {item.title && (
                <Typography variant="h4" component="h3" sx={{ p: 2.5, fontSize: '1.0625rem' }}>
                  {item.title}
                </Typography>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </HomeSection>
  );
}

function CustomSection({ section, variant }: SectionProps) {
  switch (section.type) {
    case HomeSectionType.Banners:
      return <BannersSection section={section} variant={variant} />;
    case HomeSectionType.DiarioOficial:
      return <SectionPublicationsTabs title={section.title} subtitle={section.subtitle} variant={variant} />;
    case HomeSectionType.Noticias:
      return <SectionNews title={section.title} subtitle={section.subtitle} quantity={section.itemsQuantity || DEFAULT_NEWS_QUANTITY} variant={variant} />;
    case HomeSectionType.TextoLivre:
      return <FreeTextSection section={section} variant={variant} />;
    case HomeSectionType.AcessoRapido:
      return <QuickAccessSection section={section} variant={variant} />;
    case HomeSectionType.CardDestaque:
      return <HighlightCardsSection section={section} variant={variant} />;
    case HomeSectionType.Videos:
      return <VideosSection section={section} variant={variant} />;
    default:
      return null;
  }
}

/** Home montada pela customização do APM, na ordem e com as seções visíveis que a API devolve. */
export function CustomHome({ sections }: { sections: IPortalHomeSectionResponse[] }) {
  return (
    <>
      {sections.map((section, index) => (
        // Fundos alternados para separar as seções sem blocos escuros no meio da página
        <CustomSection key={section.id} section={section} variant={index % 2 === 0 ? 'plain' : 'subtle'} />
      ))}
    </>
  );
}
